<template>
  <div>
    <!-- Resumo do Log -->
    <b-card
      v-if="logMaster"
      no-body
      class="mb-2"
    >
      <b-card-header class="bg-primary text-white d-flex justify-content-between align-items-center p-1">
        <div>
          <h4 class="mb-0 text-white">
            Resumo da Geração #{{ logId }}
          </h4>
          <small>Período: {{ formatDate(logMaster.inicio) }} - {{ formatDate(logMaster.fim) || 'Em processamento...' }}</small>
        </div>
        <b-badge :variant="statusVariant(logMaster.status)">
          {{ logMaster.status }}
        </b-badge>
      </b-card-header>

      <b-card-body class="p-2">
        <b-row class="text-center">
          <b-col
            md="3"
            sm="6"
            class="mb-1 mb-md-0"
          >
            <div class="border rounded p-1 shadow-sm">
              <h2 class="mb-0 font-weight-bolder">
                {{ logMaster.totalRegistros }}
              </h2>
              <p class="card-text text-muted mb-0">
                Total Processado
              </p>
            </div>
          </b-col>
          <b-col
            md="3"
            sm="6"
            class="mb-1 mb-md-0"
          >
            <div class="border rounded p-1 shadow-sm bg-light-success">
              <h2 class="mb-0 font-weight-bolder text-success">
                {{ logMaster.totalSucesso }}
              </h2>
              <p class="card-text text-success mb-0">
                Sucesso
              </p>
            </div>
          </b-col>
          <b-col
            md="3"
            sm="6"
            class="mb-1 mb-sm-0"
          >
            <div class="border rounded p-1 shadow-sm bg-light-warning">
              <h2 class="mb-0 font-weight-bolder text-warning">
                {{ logMaster.totalIgnorados }}
              </h2>
              <p class="card-text text-warning mb-0">
                Ignorados
              </p>
            </div>
          </b-col>
          <b-col
            md="3"
            sm="6"
          >
            <div class="border rounded p-1 shadow-sm bg-light-danger">
              <h2 class="mb-0 font-weight-bolder text-danger">
                {{ logMaster.totalErros }}
              </h2>
              <p class="card-text text-danger mb-0">
                Erros
              </p>
            </div>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Tabela de Itens com Filtros -->
    <b-card no-body>
      <div class="m-2">
        <b-row>
          <b-col
            md="6"
            class="mb-1"
          >
            <b-form-group label="Pesquisar por nome">
              <b-input-group>
                <b-form-input
                  v-model="serverParams.search"
                  placeholder="Nome do registro..."
                  @input="onFilterChange"
                />
                <b-input-group-append>
                  <b-button
                    variant="primary"
                    @click="fetchItems"
                  >
                    <feather-icon icon="SearchIcon" />
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </b-form-group>
          </b-col>
          <b-col
            md="6"
            class="mb-1"
          >
            <b-form-group label="Filtrar por Status">
              <b-form-select
                v-model="serverParams.status"
                :options="statusOptions"
                @change="onFilterChange"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </div>

      <vue-good-table
        mode="remote"
        :columns="columns"
        :rows="items"
        :total-rows="totalItems"
        :is-loading.sync="isLoading"
        :pagination-options="{
          enabled: true,
          perPage: serverParams.size,
          perPageDropdown: [10, 20, 50],
        }"
        style-class="vgt-table striped"
        @on-page-change="onPageChange"
        @on-per-page-change="onPerPageChange"
        @on-sort-change="onSortChange"
      >
        <template
          slot="table-row"
          slot-scope="props"
        >
          <span v-if="props.column.field === 'status'">
            <b-badge :variant="itemStatusVariant(props.row.status)">
              {{ props.row.status }}
            </b-badge>
          </span>
          <span v-else-if="props.column.field === 'observacao'">
            <small class="text-muted">{{ props.row.observacao || '-' }}</small>
          </span>
          <span v-else>
            {{ props.formattedRow[props.column.field] }}
          </span>
        </template>

        <div
          slot="emptystate"
          class="text-center p-2"
        >
          Nenhum registro encontrado com os filtros selecionados.
        </div>
      </vue-good-table>
    </b-card>

    <div class="mt-2">
      <b-button
        variant="outline-secondary"
        @click="$router.push({ name: 'mensalidades-logs' })"
      >
        <feather-icon
          icon="ArrowLeftIcon"
          class="mr-1"
        />
        Voltar para o Histórico
      </b-button>
    </div>
  </div>
</template>

<script>
import {
  BCard, BCardHeader, BCardBody, BRow, BCol, BBadge, BFormGroup, BFormInput, BFormSelect, BButton, BInputGroup, BInputGroupAppend,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import valorFaixaVigenciaServices from '@/api/financial/valorFaixaVigenciaServices'

export default {
  components: {
    BCard, BCardHeader, BCardBody, BRow, BCol, BBadge, BFormGroup, BFormInput, BFormSelect, BButton, BInputGroup, BInputGroupAppend, VueGoodTable,
  },
  data() {
    return {
      logId: this.$route.params.id,
      logMaster: null,
      items: [],
      totalItems: 0,
      isLoading: false,
      serverParams: {
        page: 0,
        size: 20,
        status: null,
        search: '',
        sortType: 'asc',
        sortField: 'id',
      },
      statusOptions: [
        { value: null, text: 'Todos' },
        { value: 'SUCESSO', text: 'Sucesso' },
        { value: 'IGNORADO', text: 'Ignorado' },
        { value: 'ERRO', text: 'Erros' },
      ],
      columns: [
        { label: 'ID', field: 'id', width: '80px' },
        { label: 'Registro/Nome', field: 'registroNome' },
        {
          label: 'Status', field: 'status', thClass: 'text-center', tdClass: 'text-center',
        },
        { label: 'Observações/Erro', field: 'observacao' },
      ],
    }
  },
  mounted() {
    this.fetchLogMaster()
    this.fetchItems()
  },
  methods: {
    async fetchLogMaster() {
      try {
        const { data } = await valorFaixaVigenciaServices.findLogById(this.logId)
        this.logMaster = data
      } catch (err) {
        console.error('Erro ao buscar resumo do log', err)
      }
    },
    async fetchItems() {
      this.isLoading = true
      try {
        const params = {
          page: this.serverParams.page,
          size: this.serverParams.size,
          status: this.serverParams.status,
          search: this.serverParams.search,
        }
        const { data } = await valorFaixaVigenciaServices.findLogItemsPaged(this.logId, params)
        this.items = data.content
        this.totalItems = data.totalElements
      } catch (err) {
        console.error('Erro ao buscar itens do log', err)
      } finally {
        this.isLoading = false
      }
    },
    onPageChange(params) {
      this.serverParams.page = params.currentPage - 1
      this.fetchItems()
    },
    onPerPageChange(params) {
      this.serverParams.size = params.currentPerPage
      this.fetchItems()
    },
    onSortChange() {
      // Simplificado: apenas um nível de ordenação por enquanto se necessário
      this.fetchItems()
    },
    onFilterChange() {
      this.serverParams.page = 0
      this.fetchItems()
    },
    formatDate(date) {
      if (!date) return null
      return new Date(date).toLocaleString('pt-BR')
    },
    statusVariant(status) {
      if (status === 'CONCLUIDO') return 'success'
      if (status === 'PROCESSANDO') return 'primary'
      if (status === 'ERRO') return 'danger'
      return 'warning'
    },
    itemStatusVariant(status) {
      if (status === 'SUCESSO') return 'light-success'
      if (status === 'IGNORADO') return 'light-warning'
      return 'light-danger'
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
